import { Fragment, useEffect, useState } from "react";
import { Box, Typography, List, ListItem, Collapse, Paper, Chip, Avatar } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { getData, updateData } from "../api";
import { styled } from "@mui/material/styles";

const DraggablePlayer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  marginBottom: theme.spacing(1),
  cursor: "grab",
  background: theme.palette.grey[100],
}));

interface Team {
  id: number;
  name: string;
}

interface PlayerProfile {
  id: number;
  nom_complet: string;
  equipe: string; // ex: "2,5"
  user_id: number;
  photo?: string;
}

interface Player {
  id: number;
  username: string;
  profile: PlayerProfile;
}

interface Props {
  teams: Team[];
}

export default function TeamPlayerManager({ teams }: Props) {
  const [players, setPlayers] = useState<Player[]>([]);
  const [expandedTeamIds, setExpandedTeamIds] = useState<number[]>([]);
  const [draggedPlayer, setDraggedPlayer] = useState<Player | null>(null);

  useEffect(() => {
    // Charge tous les joueurs et leur profil
    getData("users", { where: "role = 'player'" }).then(async (res: any) => {
      const users = res.data || [];
      const playersWithProfiles = await Promise.all(
        users.map(async (user: any) => {
          const profileRes = await getData("profiles", { where: `user_id = '${user.id}'` });
          const profile = profileRes.data?.[0] || { id: null, nom_complet: "", equipe: "", user_id: user.id };
          return { ...user, profile };
        })
      );
      setPlayers(playersWithProfiles);
    });
  }, []);

  const handleToggleTeam = (teamId: number) => {
    setExpandedTeamIds((prev) =>
      prev.includes(teamId) ? prev.filter((id) => id !== teamId) : [...prev, teamId]
    );
  };

  // Drag & drop natif
  const handleDragStart = (player: Player) => setDraggedPlayer(player);
  const handleDragEnd = () => setDraggedPlayer(null);

  const handleDropOnTeam = async (team: Team) => {
    if (!draggedPlayer) return;
    // Ajoute l'id d'équipe à la liste (évite doublons)
    const equipeIds = (draggedPlayer.profile.equipe || "")
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);
    if (!equipeIds.includes(team.id.toString())) {
      equipeIds.push(team.id.toString());
      const newEquipe = equipeIds.join(",");
      // Met à jour uniquement le champ equipe du profil
      await updateData("profiles", { equipe: newEquipe }, `user_id = ${draggedPlayer.id}`);
      // Rafraîchit la liste
      getData("users", { where: "role = 'player'" }).then(async (res: any) => {
        const users = res.data || [];
        const playersWithProfiles = await Promise.all(
          users.map(async (user: any) => {
            const profileRes = await getData("profiles", { where: `user_id = '${user.id}'` });
            const profile = profileRes.data?.[0] || { id: null, nom_complet: "", equipe: "", user_id: user.id };
            return { ...user, profile };
          })
        );
        setPlayers(playersWithProfiles);
      });
    }
    setDraggedPlayer(null);
  };

  // Retirer un joueur d'une équipe
  const handleRemoveFromTeam = async (player: Player, teamId: number) => {
    const equipeIds = (player.profile.equipe || "")
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean)
      .filter((id) => id !== teamId.toString());
    const newEquipe = equipeIds.join(",");
    await updateData("profiles", { equipe: newEquipe }, `user_id = ${player.id}`);
    // Rafraîchir la liste
    getData("users", { where: "role = 'player'" }).then(async (res: any) => {
      const users = res.data || [];
      const playersWithProfiles = await Promise.all(
        users.map(async (user: any) => {
          const profileRes = await getData("profiles", { where: `user_id = '${user.id}'` });
          const profile = profileRes.data?.[0] || { id: null, nom_complet: "", equipe: "", user_id: user.id };
          return { ...user, profile };
        })
      );
      setPlayers(playersWithProfiles);
    });
  };

  return (
  <Box
    sx={{
      display: "flex",
      gap: 6,
      mt: 4,
      width: '100%',
      maxWidth: 1200,
      mx: 'auto',
      flexDirection: { xs: "column", md: "row" }, // Stack vertically on mobile, row on desktop
    }}
  >
      {/* Liste des équipes */}
  <Box sx={{ flex: 1.2, minWidth: 350 }}>
    <Typography variant="h5" sx={{ mb: 2 }}>Équipes</Typography>
    <List sx={{ minWidth: 320 }}>
      {teams.map((team) => {
        const teamPlayers = players.filter((p) => (p.profile.equipe || "").split(",").includes(team.id.toString()))
          .filter((player) => !!player.profile.nom_complet && player.profile.nom_complet.trim() !== "");
        return (
          <Fragment key={team.id}>
            <ListItem
              component="div"
              onClick={() => handleToggleTeam(team.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDropOnTeam(team)}
              sx={{
                background: draggedPlayer ? "#e3f2fd" : undefined,
                borderRadius: 2,
                mb: 2,
                cursor: "pointer",
                minHeight: 90,
                alignItems: "flex-start",
                boxShadow: 2,
                border: '2px dashed #90caf9',
                transition: 'background 0.2s',
                p: 3
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{team.name}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {teamPlayers.length} élément{teamPlayers.length > 1 ? 's' : ''}
                </Typography>
              </Box>
              {expandedTeamIds.includes(team.id) ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse in={expandedTeamIds.includes(team.id)} timeout="auto" unmountOnExit>
              <Box sx={{ pl: 4, pb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: 44 }}>
                {teamPlayers.length === 0 && (
                  <Typography variant="body2" color="text.secondary">Aucun joueur</Typography>
                )}
                {teamPlayers.map((player) => (
                  <Chip
                    key={player.id}
                    label={player.profile.nom_complet}
                    avatar={
                      player.profile.photo && player.profile.photo.trim() !== ""
                        ? <Avatar src={player.profile.photo} alt={player.profile.nom_complet} />
                        : <Avatar>{player.profile.nom_complet.charAt(0)}</Avatar>
                    }
                    sx={{ m: 0.5, fontSize: 16, height: 44, minWidth: 120, pr: 1.5, pl: 0 }}
                    onDelete={() => handleRemoveFromTeam(player, team.id)}
                    deleteIcon={<CloseIcon />}
                  />
                ))}
              </Box>
            </Collapse>
          </Fragment>
        );
      })}
    </List>
  </Box>
      {/* Liste des joueurs (tous) */}
  <Box sx={{ flex: 1.1, minWidth: 350 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Joueurs</Typography>
        {players.map((player) => (
          <DraggablePlayer
            key={player.id}
            draggable
            onDragStart={() => handleDragStart(player)}
            onDragEnd={handleDragEnd}
            sx={{ opacity: draggedPlayer?.id === player.id ? 0.5 : 1, display: 'flex', alignItems: 'center', mb: 2, minHeight: 56, fontSize: 17 }}
          >
            <Avatar
              src={player.profile.photo && player.profile.photo.trim() !== "" ? player.profile.photo : undefined}
              alt={player.profile.nom_complet}
              sx={{ width: 44, height: 44, mr: 2 }}
            >
              {player.profile.nom_complet ? player.profile.nom_complet.charAt(0) : player.username.charAt(0)}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              {player.profile.nom_complet || player.username}
              <Box component="span" sx={{ display: 'block', color: "text.secondary", fontSize: 13 }}>
                {player.profile.equipe ? `Nb équipes : ${(player.profile.equipe.split(",").filter(Boolean).length)}` : "Équipes : 0"}
              </Box>
            </Box>
          </DraggablePlayer>
        ))}
      </Box>
    </Box>
  );
}
